import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { FC, useEffect, useState, useCallback } from 'react'
import { makeStyles } from 'tss-react/mui'
import {
    colors,
    MediaAsset,
    MediaAssetCategory,
    OrganizationWithMembers,
    RPUser,
} from '@hazadapt-git/public-core-base'
import OutreachHubIllustration from '../../assets/images/outreach-hub.png'
import { theme, customBorderRadius } from '../../lib/styles/universal'
import { Dialog, LoadingButton } from '../atoms'
import { getMediaAssets } from '../../lib/utils/organization'
import { useWindowSizeUp } from '../../lib/utils'
import { MediaAssetGrid } from '../organisms'

interface OutreachHubPageTemplateProps {
    user: RPUser
    fullOrganization: OrganizationWithMembers
}

export const OutreachHubPageTemplate: FC<OutreachHubPageTemplateProps> = ({
    fullOrganization,
}) => {
    const { classes: localClasses } = useLocalStyles()
    const [selectedAsset, setSelectedAsset] = useState<MediaAsset | null>(null)
    const [assets, setAssets] = useState<MediaAsset[]>([])
    const [dialogOpen, setDialogOpen] = useState(false)
    const [isLoadingAssets, setIsLoadingAssets] = useState(true)
    const mdScreens = useWindowSizeUp('md')

    useEffect(() => {
        setIsLoadingAssets(true)
        getMediaAssets()
            .then(setAssets)
            .catch(console.error)
            .finally(() => setIsLoadingAssets(false))
    }, [])

    const handleViewAsset = useCallback((asset: MediaAsset) => {
        setSelectedAsset(asset)
        setDialogOpen(true)
    }, [])

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false)
        setSelectedAsset(null)
    }, [])

    const handleDownload = useCallback(async (asset: MediaAsset | null) => {
        if (!asset) return

        try {
            const link = document.createElement('a')
            link.href = asset.url
            link.download = asset.url
            link.style.display = 'none'

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error(error)
        }
    }, [])

    return (
        <div className={localClasses.container}>
            <div className={localClasses.content}>
                <Box className={localClasses.header}>
                    {fullOrganization.logo && (
                        <img
                            src={fullOrganization.logo.url}
                            alt={
                                fullOrganization.logo.alt ||
                                fullOrganization.name + ' logo'
                            }
                            className={localClasses.orgLogo}
                        />
                    )}
                    <Typography variant="h4">
                        {fullOrganization.name}
                    </Typography>
                </Box>
                <Typography
                    variant={mdScreens ? 'h2' : 'h3'}
                    sx={{ padding: '0 1rem' }}
                    component="h1"
                >
                    Outreach Hub
                </Typography>

                <Card className={localClasses.playbookCard}>
                    <CardContent className={localClasses.playbookContent}>
                        <Box className={localClasses.playbookText}>
                            <Typography
                                variant={mdScreens ? 'h2' : 'h3'}
                                component="h1"
                            >
                                Engagement Playbook
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                                Your Guide to Outreach with HazAdapt
                            </Typography>
                            <Typography variant="body1" mb={2}>
                                Instructions, custom social media templates, and
                                ideas to boost your outreach efforts and help
                                more community members get prepared.
                            </Typography>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                disabled={
                                    !assets.some(
                                        (asset) =>
                                            asset.category ===
                                            MediaAssetCategory.PLAYBOOK
                                    )
                                }
                                onClickAsync={() => {
                                    const playbookAsset = assets.find(
                                        (asset) =>
                                            asset.category ===
                                            MediaAssetCategory.PLAYBOOK
                                    )
                                    return handleDownload(playbookAsset || null)
                                }}
                            >
                                Download PDF version
                            </LoadingButton>
                        </Box>
                        <Box className={localClasses.playbookIllustration}>
                            <img
                                src={OutreachHubIllustration}
                                alt="Outreach Hub Illustration"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    maxHeight: '100%',
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>

                <MediaAssetGrid
                    assets={assets}
                    onViewAsset={handleViewAsset}
                    onDownloadAsset={handleDownload}
                    isLoading={isLoadingAssets}
                />

                <Dialog
                    open={dialogOpen}
                    title={selectedAsset?.display_name || ''}
                    onClose={handleCloseDialog}
                    content={
                        selectedAsset ? (
                            <Box className={localClasses.dialogContent}>
                                <img
                                    src={selectedAsset?.url || ''}
                                    alt={selectedAsset?.display_name || ''}
                                    className={localClasses.dialogImage}
                                />
                            </Box>
                        ) : null
                    }
                    ctas={[
                        <LoadingButton
                            key="download"
                            variant="contained"
                            color="primary"
                            onClickAsync={async () => {
                                if (selectedAsset) {
                                    await handleDownload(selectedAsset)
                                }
                            }}
                        >
                            Download
                        </LoadingButton>,
                        <Button
                            key="close"
                            variant="outlined"
                            onClick={handleCloseDialog}
                        >
                            Close
                        </Button>,
                    ]}
                    width="60%"
                    maxWidth="40rem"
                />
            </div>
        </div>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '1rem',
        height: '100%',
        minHeight: 'calc(100dvh - 9.25rem)',
        [theme.breakpoints.up('md')]: {
            minHeight: '100dvh',
        },
    },
    content: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '1rem',
        padding: '1rem',
        width: '100%',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
    },
    orgLogo: {
        height: '4rem',
        width: '4rem',
        objectFit: 'contain',
    },
    playbookCard: {
        display: 'flex',
        borderRadius: customBorderRadius,
        boxShadow: theme.shadows[2],
        mb: 4,
        maxWidth: '60%',
        width: '100%',
        background: 'linear-gradient(90deg, #FFCC46 0%, #FF9737 100%)',
        padding: '.5rem',
        minHeight: '16rem',
    },
    playbookContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '2rem',
        width: '100%',
        backgroundColor: colors.grays.BLANC,
        borderRadius: '0.875rem',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: '.5rem',
        },
    },
    playbookText: {
        flex: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    playbookIllustration: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '10rem',
        minHeight: '12rem',
        maxHeight: 'none',
    },

    dialogContent: {
        textAlign: 'center',
        maxWidth: '100%',
        margin: '0 auto',
        padding: '1rem',
    },
    dialogImage: {
        maxWidth: '100%',
        maxHeight: '60vh',
        objectFit: 'contain',
        margin: '0 auto',
        display: 'block',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '20rem',
        width: '100%',
        padding: '2rem',
        textAlign: 'center',
    },
})
